/**
 * userFacingError - sanitize a thrown Error for display in operator-visible
 * toasts / banners. Use this whenever you would otherwise write
 *   `description: error.message`
 * in a `toast.error(...)` call.
 *
 * Truth boundary:
 *   - Edge-function and third-party API errors frequently contain raw HTTP
 *     bodies, stack traces, JSON validation paths, or HTML error pages.
 *     Showing those verbatim to an operator on a financial surface
 *     (Protection Plans, Rotessa, Approvals, Invoices) is a Trust & Truth
 *     violation: the user cannot act on it and it can leak internal field
 *     names or third-party infrastructure details.
 *   - This helper returns a short, plain-English message suitable for a
 *     toast description. It deliberately does NOT try to translate every
 *     known error code - when in doubt it falls back to a safe generic
 *     phrase and the original message is logged to the console for
 *     engineering review.
 *
 * Do not use this for messages that are already curated by our own code
 * (e.g. business-rule errors thrown from a mutation that we ourselves
 * authored with operator-friendly wording). For those, pass them through
 * as-is by calling `error.message` directly.
 */

const MAX_LENGTH = 200;

const UNSAFE_SIGNALS = [
  /<!DOCTYPE/i,
  /<html/i,
  /\bstack\b/i,
  /\bat\s+\w+\s+\(/, // stack frame
  /FunctionsHttpError/i,
  /FunctionsRelayError/i,
  /Edge Function returned a non-2xx status code/i,
  /\bTypeError\b/,
  /\bReferenceError\b/,
  /\bSyntaxError\b/,
  /Failed to fetch/i,
  /NetworkError/i,
  /ECONNREFUSED|ETIMEDOUT|ENOTFOUND/i,
];

const POLICY_SIGNALS = [
  /violates row-level security policy/i,
  /permission denied for (table|schema|relation|function)/i,
  /not authorized/i,
  /unauthorized/i,
  /forbidden/i,
];

export function userFacingError(
  err: unknown,
  fallback = "Something went wrong. Please try again or contact support."
): string {
  // Extract message + Postgres-style structured fields (hint, details, code).
  // Supabase's PostgrestError is an `Error` subclass with these extras that
  // contain the actionable reason a write was rejected. We were previously
  // dropping them, which left operators staring at a generic toast with no
  // way to act. Surfacing them is the difference between "We couldn't save
  // these changes" and "Deal is locked because a signature has been
  // collected" (hint) - same trust boundary, more truth.
  const errAny = err as Record<string, unknown> | null;
  const raw =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : typeof errAny?.message === "string"
          ? (errAny.message as string)
          : "";

  if (!raw) return fallback;

  if (POLICY_SIGNALS.some((re) => re.test(raw))) {
    return fallback;
  }

  // If the message looks like raw infrastructure / HTML / stack, hide it.
  if (UNSAFE_SIGNALS.some((re) => re.test(raw))) {
    if (typeof console !== "undefined") {
      // Preserve original detail for engineering review without exposing it
      // to the operator UI.
      console.error("[userFacingError] hidden raw error:", err);
    }
    return fallback;
  }

  // Append Postgres hint when present - that field is curated by the
  // trigger/policy author specifically to tell the caller how to fix the
  // rejection. It is safe to display.
  const hint =
    typeof errAny?.hint === "string" && errAny.hint.trim().length > 0
      ? errAny.hint.trim()
      : "";

  let composed = raw.trim();
  if (hint && !composed.toLowerCase().includes(hint.toLowerCase())) {
    composed = `${composed} - ${hint}`;
  }

  // Trim overly long messages (third-party APIs sometimes embed entire
  // request payloads in error bodies).
  if (composed.length > MAX_LENGTH) {
    return composed.slice(0, MAX_LENGTH - 3).trimEnd() + "...";
  }

  return composed;
}
